import { useState } from 'react';
import { Stack, Box } from '@mui/material';
import { Button, colors, IconButton, Tooltip } from '@sweep-io/sweep-design';
import { Delete } from '@sweep-io/sweep-design/dist/icons';
import { Prompt } from '@server/prompts';
import { MarkdownWrapper } from '../../../common/MarkdownWrapper';
import { CopyToClipboardButton } from '../copy-to-clipboard/CopyToClipboardButton';
import { UsePromptInChatButton } from '../UsePromptInChatButton';
import ConfirmDestructiveDialog from '../../../common/dialogs/ConfirmDestructiveDialog';
import { PromptWithCategory } from '../types';

interface PromptExpandableContentProps {
  prompt: PromptWithCategory;
  onDelete: ({ groupId, promptId }: { groupId: string; promptId: string }) => void;
  onTestAndEdit: (prompt: Prompt) => void;
  onUseInChatSuccess: () => void;
}

export const PromptExpandableContent = ({
  prompt,
  onDelete,
  onTestAndEdit,
  onUseInChatSuccess,
}: PromptExpandableContentProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleConfirmDelete = () => {
    onDelete?.({ groupId: prompt.categoryId, promptId: prompt.id });
    setIsDeleteDialogOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          padding: 3,
          backgroundColor: colors.white,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <MarkdownWrapper
          content={prompt.promptText}
          removeMargins={true}
          color={colors.grey[700]}
        />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderTop: `1px solid ${colors.grey[300]}`,
            paddingTop: 3,
          }}
        >
          <Stack direction="row" gap={3} alignItems="center">
            <CopyToClipboardButton textToCopy={prompt.promptText} />
            <UsePromptInChatButton promptText={prompt.promptText} onSuccess={onUseInChatSuccess} />
          </Stack>

          <Stack direction="row" gap={1} alignItems="center">
            <Tooltip title="Delete" placement="top">
              <IconButton variant="flat" size="small" onClick={() => setIsDeleteDialogOpen(true)}>
                <Delete />
              </IconButton>
            </Tooltip>
            <Button startIconName="Edit" size="small" onClick={() => onTestAndEdit?.(prompt)}>
              Test & Edit
            </Button>
          </Stack>
        </Stack>

        <ConfirmDestructiveDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Prompt"
          confirmText="Delete"
          cancelText="Cancel"
        >
          Are you sure you want to delete this prompt? This action cannot be undone.
        </ConfirmDestructiveDialog>
      </Box>
    </>
  );
};
